import * as React from 'react';
import MediaQuery from 'react-responsive';
import { Transition, TransitionGroup } from 'react-transition-group';
import Anchor from '@telia-front/anchor';
import BodyScroll from '@telia-front/body-scroll';
import Container from '@telia-front/container';
import Grid, { GridColumn } from '@telia-front/grid';
import { HeaderContext } from '@telia-front/header-provider';
import { isScrollDisabled, doScrolling, getOffsetFromParent, useCurrentBreakPoint } from '@telia-front/helper';
import Logo from '@telia-front/logo';
import PropsChecker from '@telia-front/props-checker';
import SkipLinks from '@telia-front/skip-links';
import { useTheme, breakPointOrder } from '@telia-front/theme-provider';
import HeaderActions from '../header-actions/header-actions';
import HeaderEmployee from '../header-employee/header-employee';
import HeaderLanguages from '../header-languages/header-languages';
import HeaderNav from '../header-nav/header-nav';
import HeaderOverlayNotice from '../header-overlay-notice/header-overlay-notice';
import HeaderSearch from '../header-search/header-search';
import HeaderSegment from '../header-segment/header-segment';
const Header = (props) => {
    const { media, portalRoot } = useTheme();
    const currentBreak = useCurrentBreakPoint();
    return (React.createElement(PropsChecker, { componentProps: props, componentName: "Header", defaultValueName: "defaultOpenSection", valueName: "openSection", onChangeNames: "onOpenSectionChange" },
        React.createElement(HeaderInner, Object.assign({}, props, { media: media, currentBreak: currentBreak, portalContainer: props.portalContainer !== undefined ? props.portalContainer : portalRoot }))));
};
export default Header;
export const HeaderInnerContext = React.createContext({
    containerRef: { current: null },
});
class HeaderInner extends React.Component {
    constructor(props) {
        super(props);
        this.bottomMainElement = null;
        this.element = React.createRef();
        this.prevScrollTop = 0;
        this.navDropdownContent = null;
        this.dropdownTransitionTimeout = 0;
        this.searchInput = null;
        this.containerRef = React.createRef();
        this.handleScroll = () => {
            if (!isScrollDisabled()) {
                const documentHeight = document.documentElement ? document.documentElement.offsetHeight : 0;
                const windowHeight = window.innerHeight;
                const scrollTop = Math.ceil(window.scrollY || (document.documentElement ? document.documentElement.scrollTop : 0));
                const headerHeight = this.element.current ? this.element.current.clientHeight : 0;
                if (scrollTop >= headerHeight && !this.state.hasScrolled) {
                    if (!this.state.hasScrolled) {
                        this.setState({
                            hasScrolled: true,
                        });
                    }
                }
                else if (scrollTop < headerHeight && this.state.hasScrolled) {
                    if (this.state.hasScrolled) {
                        this.setState({
                            hasScrolled: false,
                        });
                    }
                }
                // Check whether the new scrollTop is different from previous scrollTop by at least 2 pixels.
                // This difference check is needed to work around some flaky behavior with scrolling, where a 1px scrollY change triggers header to become visible again.
                const isScrollingTop = this.prevScrollTop - scrollTop >= 2;
                if ((isScrollingTop && !this.state.isScrollingTop) || windowHeight + scrollTop >= documentHeight - headerHeight) {
                    this.setState({
                        isScrollingTop: true,
                    });
                }
                else if (this.prevScrollTop < scrollTop && this.state.isScrollingTop) {
                    this.setState({
                        isScrollingTop: false,
                    });
                }
                this.prevScrollTop = scrollTop;
            }
        };
        this.handleResize = () => {
            this.setHeaderHeight();
        };
        this.handleMinimizedSearch = () => {
            if (this.isSearchOverlappingNav()) {
                this.setState({
                    isSearchMinimized: true,
                });
            }
            else if (this.state.openSection === 'search') {
                this.setState({
                    isSearchMinimized: false,
                });
            }
        };
        this.isSearchOverlappingNav = () => {
            var _a;
            const headerNavElement = document.getElementById('header-nav');
            let headerSearchElement;
            if ((_a = this.props.search) === null || _a === void 0 ? void 0 : _a.id) {
                headerSearchElement = document.getElementById(this.props.search.id);
            }
            else {
                headerSearchElement = document.getElementById('header-search');
            }
            const navRect = headerNavElement && headerNavElement.getBoundingClientRect();
            const searchRect = headerSearchElement && headerSearchElement.getBoundingClientRect();
            if (navRect && searchRect) {
                return !(navRect.top > searchRect.bottom || navRect.right < searchRect.left || navRect.bottom < searchRect.top || navRect.left > searchRect.right);
            }
        };
        this.onActionClick = (section) => {
            const prevOpenSection = this.openSection;
            const nextOpenSection = prevOpenSection === section ? null : section;
            this.setState({
                openAnimatingSection: nextOpenSection,
            });
            this.onOpenSectionChange(nextOpenSection, prevOpenSection);
            if (nextOpenSection === 'search' && this.searchInput) {
                window.setTimeout(() => {
                    this.searchInput && this.searchInput.focus();
                });
            }
        };
        this.closeEverything = () => {
            const prevOpenSection = this.openSection;
            if (prevOpenSection) {
                this.onOpenSectionChange(null, prevOpenSection);
            }
        };
        this.handleOverlayExited = () => {
            if (this.state.isOverlayVisible) {
                this.setState({
                    isOverlayVisible: false,
                });
            }
        };
        this.handleOverlayEntered = () => {
            if (!this.state.isOverlayVisible) {
                this.setState({
                    isOverlayVisible: true,
                });
            }
        };
        this.handleItemClick = (element) => {
            if (this.navDropdownContent && element) {
                doScrolling(getOffsetFromParent(element, this.navDropdownContent).top, 500, this.navDropdownContent);
            }
        };
        this.handleItemToggle = (isAnySubNavOpen) => {
            const prevOpenSection = this.openSection;
            if (!isAnySubNavOpen) {
                // only close section when prev section is nav - if prev section is not nav, then it is already changed to something else
                if (window.matchMedia(`(min-width: ${this.props.media.width.lg.min})`).matches && prevOpenSection === 'nav') {
                    this.onOpenSectionChange(null, prevOpenSection);
                }
            }
            else {
                this.setState({
                    openAnimatingSection: 'nav',
                });
                this.onOpenSectionChange('nav', prevOpenSection);
            }
        };
        this.handleDropdownTransitionExited = (id) => {
            if (this.state.openAnimatingSection === id || !id) {
                this.setState({
                    openAnimatingSection: null,
                });
            }
        };
        this.handleDropdownTransitionEntered = (section) => {
            this.dropdownTransitionTimeout = window.setTimeout(() => {
                this.setState({
                    openAnimatingSection: section,
                });
            }, 100);
        };
        this.handleNavDropdownTransitionEntered = () => {
            this.handleDropdownTransitionEntered('nav');
        };
        this.onMediaQueryChange = (matches) => {
            this.setState({
                isLargeScreen: matches,
            });
        };
        this.handleBottomMainEntered = () => {
            if (this.state.isBottomMainVisible) {
                this.setState({
                    isBottomMainVisible: false,
                });
            }
        };
        this.handleBottomMainExited = () => {
            if (!this.state.isBottomMainVisible) {
                this.setState({
                    isBottomMainVisible: true,
                });
            }
        };
        this.shouldShowSection = (section) => {
            return this.openSection === section && this.state.isBottomMainVisible && this.state.isMounted;
        };
        this.handleFocus = (event) => {
            var _a;
            // if the element receiving focus IS inside header
            if (((_a = this.element.current) === null || _a === void 0 ? void 0 : _a.contains(event.target)) && !this.state.isFocusInside) {
                this.setState({
                    isFocusInside: true,
                });
            }
        };
        this.handleBlur = (event) => {
            var _a;
            // if the element receiving focus IS NOT inside header
            if (!((_a = this.element.current) === null || _a === void 0 ? void 0 : _a.contains(event.relatedTarget)) && this.state.isFocusInside) {
                this.setState({
                    isFocusInside: false,
                });
            }
        };
        this.state = {
            disableBodyScroll: false,
            hasScrolled: false,
            isBottomMainVisible: true,
            isLargeScreen: false,
            isMounted: false,
            isOverlayVisible: false,
            isScrollingTop: false,
            openAnimatingSection: props.defaultOpenSection || null,
            openSection: props.defaultOpenSection || null,
            isSearchMinimized: false,
            isFocusInside: false,
        };
    }
    componentDidMount() {
        this.prevScrollTop = window.scrollY || (document.documentElement ? document.documentElement.scrollTop : 0);
        window.addEventListener('scroll', this.handleScroll);
        window.addEventListener('resize', this.handleResize);
        this.toggleScroll();
        this.setState({
            isLargeScreen: window.matchMedia(`(min-width: ${this.props.media.width.lg.min})`).matches,
            isMounted: true,
        });
        this.context.toggleHeaderHidden(this.isBottomMainHidden());
        if (this.bottomMainElement) {
            this.context.setHeaderBottomHeight(this.bottomMainElement.clientHeight);
        }
        this.setHeaderHeight();
        this.handleMinimizedSearch();
    }
    componentWillUnmount() {
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleResize);
        window.clearTimeout(this.dropdownTransitionTimeout);
    }
    componentDidUpdate(prevProps, prevState) {
        const hasStateChanged = typeof this.props.openSection === 'undefined' && prevState.openSection !== this.state.openSection;
        const hasPropsChanged = typeof this.props.openSection !== 'undefined' && prevProps.openSection !== this.props.openSection;
        if (hasPropsChanged && this.props.openSection) {
            this.setState({
                openAnimatingSection: this.props.openSection,
            });
        }
        if (this.state.isLargeScreen && !prevState.isLargeScreen && this.openSection === 'nav') {
            this.onOpenSectionChange(null, 'nav');
        }
        // since search does not use Transition that usually nulls openAnimatingSection when necessary,
        // we need to do this manually here when controlled openSection prop changes from 'search' to null.
        if ((hasPropsChanged && prevProps.openSection === 'search') || (hasStateChanged && prevState.openSection === 'search')) {
            this.setState({
                openAnimatingSection: null,
            });
        }
        if (hasStateChanged || hasPropsChanged) {
            if (this.openSection === 'search') {
                return;
            }
            this.toggleScroll();
        }
        this.context.toggleHeaderHidden(this.isBottomMainHidden());
        if (this.bottomMainElement) {
            this.context.setHeaderBottomHeight(this.bottomMainElement.clientHeight);
        }
        // do not trigger onDidUpdate when hasScrolled is true because
        // this will enter a padding update loop in MainHeader
        if (!this.state.hasScrolled) {
            this.setHeaderHeight();
        }
    }
    get openSection() {
        return this.isOpenSectionControlled(this.props.openSection) ? this.props.openSection : this.state.openSection;
    }
    isOpenSectionControlled(value = this.props.openSection) {
        return !!this.props.onOpenSectionChange && typeof value !== 'undefined';
    }
    onOpenSectionChange(nextOpenSection, prevOpenSection) {
        if (!this.isOpenSectionControlled()) {
            this.setState({
                openSection: nextOpenSection,
            });
        }
        if (this.props.onOpenSectionChange) {
            this.props.onOpenSectionChange(nextOpenSection, prevOpenSection);
        }
    }
    disableBodyScroll() {
        if (!this.state.disableBodyScroll) {
            this.setState({
                disableBodyScroll: true,
            });
        }
    }
    enableBodyScroll() {
        if (this.state.disableBodyScroll) {
            this.setState({
                disableBodyScroll: false,
            });
        }
    }
    toggleScroll() {
        if (this.openSection) {
            this.disableBodyScroll();
        }
        else {
            this.enableBodyScroll();
        }
    }
    setHeaderHeight() {
        if (this.element.current) {
            this.context.setHeaderHeight(this.element.current.clientHeight);
        }
    }
    BEM() {
        const classArray = ['header'];
        if (this.isBottomMainHidden()) {
            classArray.push('has-scrolled');
        }
        if (this.openSection) {
            classArray.push('header--open-' + this.openSection);
        }
        if (this.state.openAnimatingSection) {
            classArray.push('header--animating-' + this.state.openAnimatingSection);
        }
        if (this.state.isOverlayVisible) {
            classArray.push('header--has-overlay');
        }
        if (this.props.className) {
            classArray.push(this.props.className);
        }
        if (this.state.isSearchMinimized) {
            classArray.push('header--minimized-search');
        }
        return classArray.join(' ');
    }
    shouldShowOverlay() {
        const { actions } = this.props;
        if (this.openSection && actions) {
            const currentAction = actions.items.find(a => a.id === this.openSection);
            if (currentAction) {
                return typeof currentAction.showOverlay === 'undefined' ? true : currentAction.showOverlay;
            }
        }
        return false;
    }
    getOverlay() {
        return (React.createElement(Transition, { in: this.shouldShowOverlay(), timeout: { enter: 10, exit: 100 }, mountOnEnter: true, unmountOnExit: true, onExited: this.handleOverlayExited, onEntering: this.handleOverlayEntered }, (state) => {
            return React.createElement("div", { className: 'header__overlay is-' + state, onClick: this.closeEverything });
        }));
    }
    getNav(nav, secondaryItemsBefore = false) {
        return (React.createElement(HeaderNav, Object.assign({}, nav, { isOpen: this.openSection === 'nav', onItemClick: this.handleItemClick, onItemToggle: this.handleItemToggle, dropdownContainer: this.dropdownContainer, onMegamenuDropdownTransitionExited: this.handleDropdownTransitionExited, onMegamenuDropdownTransitionEntered: this.handleNavDropdownTransitionEntered, secondaryItemsBefore: secondaryItemsBefore })));
    }
    getActions(actions, location) {
        return (React.createElement(HeaderActions, Object.assign({}, actions, { onActionClick: this.onActionClick, currentSection: this.openSection, currentAnimatingSection: this.state.openAnimatingSection, dropdownContainer: this.dropdownContainer, onDropdownClose: this.closeEverything, onDropdownTransitionExited: this.handleDropdownTransitionExited, checkIfDropdownShouldBeVisible: this.shouldShowSection, dropdownOffsetY: 8, nav: {
                dropdownContent: this.renderNavDropdownContent(),
                dropdownPadding: 'none',
                dropdownRef: (element) => (this.navDropdownContent = element),
            }, location: location })));
    }
    renderAccessibilityLink(accessibility) {
        const { label, url, onClick } = accessibility;
        return (React.createElement(Anchor, { className: "header__accessibility text-weight-normal", url: url, onClick: onClick, isTextColor: true, isUnderlined: false }, label));
    }
    renderNavDropdownContent() {
        return (React.createElement(React.Fragment, null,
            React.createElement(Container, { className: "header__dropdown-content-top" },
                React.createElement(Grid, { noWrap: true, align: ['middle-xs'], gutter: "none" },
                    React.createElement(GridColumn, { width: ['min'] }, this.props.accessibility && this.renderAccessibilityLink(this.props.accessibility)),
                    React.createElement(GridColumn, { width: ['max'] }, this.props.languages && React.createElement(HeaderLanguages, Object.assign({}, this.props.languages))))),
            this.props.nav && this.getNav(this.props.nav, true)));
    }
    getSearch(headerSearch) {
        const BEM = () => {
            const classArray = ['header__bottom-search-wrapper'];
            if (this.shouldShowSection('search')) {
                classArray.push('is-open');
            }
            return classArray.join(' ');
        };
        const handleSearchFocus = () => {
            const nextOpenSection = 'search';
            this.setState({
                openAnimatingSection: nextOpenSection,
            });
            this.onOpenSectionChange(nextOpenSection, this.openSection);
            if (headerSearch.onFocus) {
                headerSearch.onFocus();
            }
        };
        const handleOnSubmit = (event) => {
            if (!this.searchInput || this.searchInput.value.length < 1) {
                event.preventDefault();
                if (this.openSection !== 'search') {
                    this.onActionClick('search');
                    return;
                }
                const openSection = 'search';
                this.setState({
                    openAnimatingSection: openSection,
                });
                this.onOpenSectionChange(openSection, this.openSection);
                window.setTimeout(() => {
                    this.searchInput && this.searchInput.focus();
                });
            }
            if (headerSearch.onSubmit) {
                headerSearch.onSubmit(event);
            }
        };
        const setInputRef = (element) => {
            this.searchInput = element;
            if (headerSearch.inputRef) {
                headerSearch.inputRef(element);
            }
        };
        return (React.createElement("div", { className: BEM() },
            React.createElement(HeaderSearch, Object.assign({}, headerSearch, { onFocus: handleSearchFocus, onSubmit: handleOnSubmit, inputRef: setInputRef }))));
    }
    topRightBEM() {
        var _a, _b;
        const classArray = ['header__top-right'];
        if ((_b = (_a = this.props.actions) === null || _a === void 0 ? void 0 : _a.sso) === null || _b === void 0 ? void 0 : _b.isLoggedIn) {
            classArray.push('logged-in');
        }
        return classArray.join(' ');
    }
    // Returns all actions' locations as a responsive object
    refactoredActions() {
        if (this.props.actions) {
            return Object.assign(Object.assign({}, this.props.actions), { items: this.props.actions.items.map((item) => {
                    if (item.location === 'all') {
                        // Cart and notifications have location="all"
                        return Object.assign(Object.assign({}, item), { location: { xs: 'bottom', lg: 'top' } });
                    }
                    else if (item.id === 'nav') {
                        // Do not render nav burger in desktop
                        return Object.assign(Object.assign({}, item), { location: { xs: 'bottom', lg: null } });
                    }
                    else if (typeof item.location === 'string') {
                        return Object.assign(Object.assign({}, item), { location: { xs: item.location } });
                    }
                    else if (!item.location) {
                        return Object.assign(Object.assign({}, item), { location: { xs: 'bottom' } });
                    }
                    else {
                        return Object.assign(Object.assign({}, item), { location: item.location });
                    }
                }) });
        }
    }
    // Returns actions' location based on its responsive location object and current breakpoint
    getLocation(currentBreak, locations) {
        const remToNumber = (value) => {
            return parseFloat(value.split('rem')[0]);
        };
        let currentLocation = null;
        if (currentBreak && locations) {
            for (let i = 0; i < breakPointOrder.length; i++) {
                const breakpoint = breakPointOrder[i];
                if (remToNumber(this.props.media.width[breakpoint].min) <= remToNumber(this.props.media.width[currentBreak].min)) {
                    if (locations[breakpoint] !== undefined) {
                        if (locations[breakpoint] === null) {
                            return null; // Hide from this breakpoint onwards
                        }
                        currentLocation = locations[breakpoint];
                    }
                }
                else {
                    break;
                }
            }
            return currentLocation;
        }
    }
    getTop() {
        const refactoredActions = this.refactoredActions();
        const getTopActions = () => {
            if (refactoredActions) {
                return Object.assign(Object.assign({}, refactoredActions), { items: refactoredActions.items.filter(({ location }) => this.getLocation(this.props.currentBreak, location) === 'top') });
            }
        };
        const topActions = getTopActions();
        return (React.createElement("div", { className: "header__top" },
            React.createElement(Container, null,
                React.createElement("div", { className: "header__top-inner" },
                    React.createElement("div", { className: "header__top-left" }, this.props.segment && React.createElement(HeaderSegment, Object.assign({ scrollable: true }, this.props.segment))),
                    React.createElement("div", { className: this.topRightBEM() },
                        this.props.accessibility && this.renderAccessibilityLink(this.props.accessibility),
                        this.props.languages && React.createElement(HeaderLanguages, Object.assign({}, this.props.languages)),
                        (topActions === null || topActions === void 0 ? void 0 : topActions.items.length) ? this.getActions(topActions, 'top') : undefined)))));
    }
    getEmployeeHeader() {
        if (!this.props.employee) {
            return null;
        }
        return (React.createElement(HeaderEmployee, Object.assign({}, this.props.employee, { openSection: this.openSection, openAnimatingSection: this.state.openAnimatingSection, onActionClick: this.onActionClick, onDropdownClose: this.closeEverything, dropdownContainer: this.dropdownContainer, onDropdownTransitionExited: this.handleDropdownTransitionExited })));
    }
    get dropdownContainer() {
        var _a;
        return this.state.isMounted ? ((_a = this.props.portalContainer) !== null && _a !== void 0 ? _a : document.body) : null;
    }
    isBottomMainHidden() {
        return this.state.hasScrolled && !this.state.isScrollingTop && !this.openSection && !this.state.openAnimatingSection && !this.state.isFocusInside;
    }
    logoBEM() {
        const { logo } = this.props;
        const classArray = ['header__logo'];
        if (logo && logo.className) {
            classArray.push(logo.className);
        }
        return classArray.join(' ');
    }
    shouldShowTop() {
        const { accessibility, segment, languages } = this.props;
        const refactoredActions = this.refactoredActions();
        if (!segment && !languages && !accessibility) {
            if (!refactoredActions) {
                return false;
            }
            if (refactoredActions && refactoredActions.items.length) {
                return refactoredActions.items.some(i => this.getLocation(this.props.currentBreak, i.location) === 'top');
            }
        }
        return true;
    }
    getBottomMain() {
        const notices = Array.isArray(this.props.overlayNotice) ? this.props.overlayNotice : this.props.overlayNotice ? [this.props.overlayNotice] : [];
        const refactoredActions = this.refactoredActions();
        const getBottomActions = () => {
            if (refactoredActions) {
                return Object.assign(Object.assign({}, refactoredActions), { items: refactoredActions.items.filter(({ location }) => this.getLocation(this.props.currentBreak, location) === 'bottom') });
            }
        };
        const bottomActions = getBottomActions();
        const content = (state) => (React.createElement("div", { className: "header__bottom-main", "data-state": state, ref: element => (this.bottomMainElement = element) },
            this.shouldShowTop() && this.getTop(),
            React.createElement("div", { className: "header__bottom" },
                React.createElement(Container, { ref: this.containerRef },
                    React.createElement("div", { className: "header__bottom-inner" },
                        React.createElement("div", { className: "header__bottom-left" },
                            this.props.logo && React.createElement(Logo, Object.assign({}, this.props.logo, { className: this.logoBEM(), size: "small" })),
                            this.props.nav && this.getNav(this.props.nav)),
                        React.createElement("div", { className: "header__bottom-right" },
                            React.createElement("div", { className: "header__bottom-right-search" }, this.props.search && this.getSearch(this.props.search)),
                            (bottomActions === null || bottomActions === void 0 ? void 0 : bottomActions.items.length) ? this.getActions(bottomActions, 'bottom') : undefined))),
                React.createElement("div", { className: "header__bottom-shadow" })),
            React.createElement("div", { "aria-live": "polite", role: "status" },
                React.createElement(TransitionGroup, { className: "header__overlay-notices-wrapper" }, notices.map((notice, index) => {
                    return (React.createElement(HeaderOverlayNotice, { key: notice.id ? `${notice.id}-overlay-notice` : index, timeout: { enter: 500, exit: 500 }, classNames: {
                            enter: 'is-entering',
                            enterActive: 'is-entered',
                            exit: 'is-exiting',
                            exitActive: 'is-exited',
                        }, notice: notice }));
                })))));
        return (React.createElement(Transition, { in: this.isBottomMainHidden(), timeout: { enter: 10, exit: 500 }, onExited: this.handleBottomMainExited, onEntered: this.handleBottomMainEntered }, content));
    }
    render() {
        return (React.createElement(BodyScroll, { isDisabled: this.state.disableBodyScroll },
            React.createElement(HeaderInnerContext.Provider, { value: { containerRef: this.containerRef } },
                React.createElement("header", { className: this.BEM(), ref: this.element, id: "header", onFocus: this.handleFocus, onBlur: this.handleBlur },
                    this.props.skipLinks && this.props.skipLinks.length > 0 && (React.createElement(SkipLinks, { links: this.props.skipLinks, setHeaderHeight: () => this.setHeaderHeight() })),
                    React.createElement("div", { className: "header__main" },
                        this.props.employee && this.getEmployeeHeader(),
                        this.getBottomMain()),
                    this.getOverlay(),
                    this.state.isMounted && (React.createElement(MediaQuery, { minWidth: this.props.media.width.lg.min, onChange: this.onMediaQueryChange }, null))))));
    }
}
HeaderInner.contextType = HeaderContext;
